// Behaviour shared by the floating primitives (Modal, Sheet, ConfirmModal,
// menus, dropdown lists): focus trapping, scroll locking and positioning a
// portaled panel against its trigger.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

// Tab stops only: a control taken out of the Tab order (tabindex="-1", as
// the unselected options of a roving radio group are) is skipped, so a
// dialog never opens with a ring on an option that is not the chosen one.
export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute("inert") && el.getAttribute("tabindex") !== "-1" && el.getClientRects().length > 0,
  );
}

function isTextEntry(el: HTMLElement): boolean {
  if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
  if (el.tagName !== "INPUT") return false;
  const t = (el as HTMLInputElement).type;
  return !["checkbox", "radio", "range", "color", "file", "button", "submit", "reset"].includes(t);
}

// Was the last user input a pointer (mouse, touch, pen) rather than a key?
// A dialog opened from a click on a plain row would otherwise draw a focus
// ring on its first button the moment it appears (the browser treats
// focus moved by script from <body> as keyboard focus).
let pointerLast = false;
if (typeof document !== "undefined") {
  document.addEventListener("pointerdown", () => (pointerLast = true), true);
  document.addEventListener("keydown", () => (pointerLast = false), true);
}

/** True when the latest user input was a pointer press, not a key. */
export function lastInputWasPointer(): boolean {
  return pointerLast;
}

// Open traps (= open dialogs and sheets), innermost last: only the top
// one handles Tab and Escape, so a confirm or dialog opened over another
// does not fight it for focus or close it too.
const trapStack: HTMLElement[] = [];

/** True when `el` is the innermost open dialog / sheet panel. */
export function isTopLayer(el: HTMLElement | null): boolean {
  return !!el && trapStack[trapStack.length - 1] === el;
}

/**
 * While `active`: move focus into `ref`, keep Tab / Shift+Tab inside it,
 * and give focus back to whatever had it before when it closes.
 *
 * Where focus starts: the `initialFocus` element, else the first
 * [data-autofocus], else the first control inside [data-dialog-body] (so a
 * form dialog starts on its first field, not on the header's Close
 * button), else the panel itself. Two exceptions keep pointer users free
 * of surprises: when the dialog was opened with the mouse or a finger,
 * only a text field is focused (its caret is expected; a ring on a button
 * is not), and on touch screens not even that (it would pop the keyboard
 * over the sheet) — the panel takes focus instead, which screen readers
 * still announce.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement>,
  active: boolean,
  initialFocus?: RefObject<HTMLElement>,
) {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const previous = document.activeElement as HTMLElement | null;
    trapStack.push(root);
    const body = root.querySelector<HTMLElement>("[data-dialog-body]");
    const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
    const first = focusableIn(body ?? root)[0];
    const auto =
      !first || coarse || (pointerLast && !isTextEntry(first)) ? root : first;
    const target =
      initialFocus?.current ?? root.querySelector<HTMLElement>("[data-autofocus]") ?? auto;
    // After paint, so a portal that just mounted can take focus.
    const raf = requestAnimationFrame(() => {
      if (!root.contains(document.activeElement)) target.focus({ preventScroll: true });
    });
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Tab" || !root || trapStack[trapStack.length - 1] !== root) return;
      const items = focusableIn(root);
      if (!items.length) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (e.shiftKey && (current === first || !root.contains(current))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || !root.contains(current))) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey, true);
      const at = trapStack.lastIndexOf(root);
      if (at >= 0) trapStack.splice(at, 1);
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

// Nested overlays share one lock: the page scrolls again only when the
// last of them closes.
let lockCount = 0;
let savedOverflow = "";

export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    if (lockCount === 0) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    lockCount += 1;
    return () => {
      lockCount -= 1;
      if (lockCount === 0) document.body.style.overflow = savedOverflow;
    };
  }, [active]);
}

export interface AnchoredPosition {
  top: number;
  left: number;
  minWidth: number;
  /** Set when the panel is fitted to the anchor (`fit`): its exact width. */
  width?: number;
  maxHeight: number;
  placement: "below" | "above";
}

/** `fit: "auto"` fits the panel to an anchor at least this wide. */
const AUTO_FIT_MIN = 240;

/**
 * Fixed-position coordinates for a portaled panel under (or, when there is
 * no room, above) `anchor`, kept inside the viewport and re-measured on
 * scroll, resize and when the panel itself changes size. `align` lines the panel's start or end edge up with
 * the anchor's; `matchWidth` makes it at least as wide as the anchor.
 * `fit` makes it EXACTLY as wide as the anchor, start edges aligned (the
 * caller applies `width` so long content wraps instead of widening the
 * panel past the field); "auto" fits only an anchor at least 240px wide
 * (a form field), and leaves a compact trigger's list content-sized.
 */
export function useAnchoredPosition(
  anchor: RefObject<HTMLElement>,
  panel: RefObject<HTMLElement>,
  open: boolean,
  { align = "start", matchWidth = true, minWidth = 0, offset = 4, fit = false }: {
    align?: "start" | "end";
    matchWidth?: boolean;
    minWidth?: number;
    offset?: number;
    fit?: boolean | "auto";
  } = {},
): AnchoredPosition | null {
  const [pos, setPos] = useState<AnchoredPosition | null>(null);
  const frame = useRef(0);

  const measure = useCallback(() => {
    const a = anchor.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const p = panel.current?.getBoundingClientRect();
    const pad = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const fitted = fit === true || (fit === "auto" && r.width >= Math.max(AUTO_FIT_MIN, minWidth));
    const width = fitted
      ? Math.min(r.width, vw - 2 * pad)
      : Math.max(p?.width ?? 0, matchWidth ? r.width : 0, minWidth);
    const height = p?.height ?? 0;
    const below = vh - r.bottom - offset - pad;
    const above = r.top - offset - pad;
    const placement: "below" | "above" = height > below && above > below ? "above" : "below";
    const maxHeight = Math.max(120, placement === "below" ? below : above);
    const shown = Math.min(height, maxHeight);
    let left = align === "end" && !fitted ? r.right - width : r.left;
    left = Math.max(pad, Math.min(left, vw - width - pad));
    const top = placement === "below" ? r.bottom + offset : r.top - offset - shown;
    setPos({
      top: Math.max(pad, top),
      left,
      minWidth: fitted ? width : Math.max(matchWidth ? r.width : 0, minWidth),
      width: fitted ? width : undefined,
      maxHeight,
      placement,
    });
  }, [anchor, panel, align, matchWidth, minWidth, offset, fit]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    measure();
    // Second pass once the panel has rendered at its real size.
    frame.current = requestAnimationFrame(measure);
    const onMove = () => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(measure);
    };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    // The panel changes size when its options change while open.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onMove) : null;
    if (panel.current) ro?.observe(panel.current);
    return () => {
      cancelAnimationFrame(frame.current);
      ro?.disconnect();
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, measure]);

  return pos;
}

// Portaled floating panels (a dropdown's list, a menu) open right now, in
// the order they opened. A panel opened from inside a popover lives on
// document.body, outside the popover's element, so a press on one of its
// options would otherwise count as a press outside the popover and close
// it before the option's click lands.
const floatingLayers: HTMLElement[] = [];

/** While `active`, `ref`'s element is a floating layer (see useOutsidePress). */
export function useFloatingLayer(ref: RefObject<HTMLElement>, active: boolean) {
  useEffect(() => {
    const el = ref.current;
    if (!active || !el) return;
    floatingLayers.push(el);
    return () => {
      const at = floatingLayers.lastIndexOf(el);
      if (at >= 0) floatingLayers.splice(at, 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

/**
 * Calls `onOutside` for a pointer press outside every one of `refs`. A
 * press inside a floating layer (useFloatingLayer) that opened after this
 * one became active is not outside: it is a list opened from within it.
 */
export function useOutsidePress(
  refs: RefObject<HTMLElement>[],
  active: boolean,
  onOutside: () => void,
) {
  const cb = useRef(onOutside);
  cb.current = onOutside;
  useEffect(() => {
    if (!active) return;
    const before = new Set(floatingLayers);
    function onDown(e: PointerEvent) {
      const t = e.target as Node;
      if (refs.some((r) => r.current?.contains(t))) return;
      if (floatingLayers.some((layer) => !before.has(layer) && layer.contains(t))) return;
      cb.current();
    }
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
